/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — ProductionEcrite UI (Track J.3b — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth : shared/mte-product/03-skill-taxonomy.md
 * Owner : Atelier (Track J.3b)
 *
 * Archetype : "production-ecrite" (textarea + flag "needs parent grading").
 * evalMode : always 'HUMAN' — parent or teacher evaluates the open response.
 *
 * Props (canonical, see lib/banque-types.ts):
 *   - item          : BanqueItemProductionEcrite
 *   - onAnswer      : (correct: boolean, payload?: unknown) => void
 *                     Called with correct=true once parent-grading flag accepted.
 *                     (UI does not auto-evaluate text content.)
 *   - disabled?     : boolean
 *   - showFeedback? : 'idle' | 'correct' | 'incorrect'
 *
 * Payload returned via onAnswer : { textLength: number, wordCount: number, minWords: number }
 * ========================================================================== */

import React, { useState, useMemo } from 'react';
import type { BanqueItemProductionEcrite } from '../../../../lib/banque-types';

export interface ProductionEcriteProps {
  item: BanqueItemProductionEcrite;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/u).length;
}

export function ProductionEcrite({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: ProductionEcriteProps): JSX.Element {
  const [text, setText] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [pendingValidation, setPendingValidation] = useState<boolean>(false);

  const wordCount = useMemo(() => countWords(text), [text]);
  const meetsMin = wordCount >= item.minWords;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (disabled || submitted || !meetsMin) return;
    setSubmitted(true);
    setPendingValidation(true);
    // Always report 'pending parent grading' (not auto-graded).
    onAnswer(true, {
      textLength: text.length,
      wordCount,
      minWords: item.minWords,
      status: 'pending_parent_grading',
    });
  };

  const handleParentValidated = (): void => {
    if (!pendingValidation || disabled) return;
    setPendingValidation(false);
    onAnswer(true, {
      textLength: text.length,
      wordCount,
      minWords: item.minWords,
      status: 'parent_validated',
    });
  };

  const feedbackClass = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : 'pending'
    : 'idle';

  return (
    <form
      className="production-ecrite"
      data-testid="production-ecrite"
      data-archetype="production-ecrite"
      data-item-id={item.id}
      data-eval-mode="HUMAN"
      data-feedback={feedbackClass}
      onSubmit={handleSubmit}
    >
      <p className="production-ecrite-enonce" aria-label="Énoncé">
        {item.enonce}
      </p>
      {item.consigne && (
        <p className="production-ecrite-consigne">{item.consigne}</p>
      )}
      <label className="production-ecrite-label" htmlFor={`pe-textarea-${item.id}`}>
        Ta réponse&nbsp;:
        <textarea
          id={`pe-textarea-${item.id}`}
          className="production-ecrite-textarea"
          data-testid="production-ecrite-textarea"
          rows={6}
          disabled={disabled || submitted}
          placeholder={item.placeholder ?? 'Écris ta réponse ici…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-describedby={`pe-counter-${item.id}`}
        />
      </label>
      <p
        id={`pe-counter-${item.id}`}
        className="production-ecrite-counter"
        data-testid="production-ecrite-counter"
        aria-live="polite"
      >
        {wordCount} mot{wordCount !== 1 ? 's' : ''} / minimum {item.minWords}
      </p>
      <button
        type="submit"
        className="production-ecrite-submit"
        data-action="soumettre"
        data-testid="production-ecrite-submit"
        disabled={!meetsMin || submitted || disabled}
      >
        Soumettre pour validation parent
      </button>
      {submitted && (
        <div
          className="production-ecrite-pending"
          data-testid="production-ecrite-pending"
          role="status"
        >
          <p>
            En attente de validation parent. Un adulte vérifiera ta production écrite.
          </p>
          <button
            type="button"
            className="production-ecrite-parent-validated"
            data-action="parent-validated"
            data-testid="production-ecrite-parent-validated"
            onClick={handleParentValidated}
            disabled={disabled || !pendingValidation}
          >
            Marquer comme validé par un parent
          </button>
        </div>
      )}
    </form>
  );
}

export default ProductionEcrite;
